"use client";

/**
 * fund-upgrade-cta.tsx
 *
 * Full-width gradient-bordered upgrade card shown to FREE users in place of
 * paywalled PRO sections (Risk Analysis and WTM Assessment). Displays locked
 * feature pills and a CTA that links to /pricing.
 *
 * Used in: src/app/funds/[slug]/page.tsx (replaces Risk + WTM for free tier)
 */

import Link from "next/link";
import { Lock, ArrowRight } from "lucide-react";

export interface FundUpgradeCtaProps {
  fundName?: string;
}

const FEATURES = [
  "Max drawdown analysis",
  "WTM Fund Score (1-5)",
  "6 sub-scores breakdown",
  "Risk-adjusted metrics",
  "Peer comparison",
  "Fund manager deep-dive",
];

export function FundUpgradeCta({ fundName }: FundUpgradeCtaProps) {
  return (
    <section className="mt-6">
      <div
        className="relative rounded-xl p-6 md:p-8 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(59,130,246,0.06) 100%)",
          border: "1px solid rgba(245,158,11,0.3)",
        }}
      >
        {/* Lock icon */}
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: "rgba(245,158,11,0.15)" }}
        >
          <Lock className="w-5 h-5 text-gold" />
        </div>

        {/* Heading */}
        <h2 className="text-foreground text-xl md:text-2xl font-serif mb-2">
          Unlock the full analysis
          {fundName ? (
            <span className="text-muted-foreground font-normal"> for {fundName}</span>
          ) : null}
        </h2>
        <p className="text-muted-foreground text-sm mb-5 max-w-2xl">
          Upgrade to PRO to see risk analysis, drawdown history, the WTM Fund
          Score, and all 6 sub-scores, plus unlimited reports across stocks
          and funds.
        </p>

        {/* Feature pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FEATURES.map((f) => (
            <span
              key={f}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-surface border border-surface-light/15 text-muted-foreground"
            >
              <Lock className="w-3 h-3 text-gold" />
              {f}
            </span>
          ))}
        </div>

        {/* CTA */}
        <Link
          href="/pricing"
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-black text-sm font-bold px-5 py-2.5 rounded-lg transition-colors"
        >
          Upgrade to PRO
          <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="text-dim text-[11px] mt-3">
          $9.99/month · Cancel anytime
        </div>
      </div>
    </section>
  );
}
